import { useEffect, useState } from 'react';
import { DashboardLayout } from '@/components/DashboardLayout';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { GraduationCap, Plus, Trash2, Users, Award, Calendar, DollarSign, BookOpen } from 'lucide-react';
import api from '@/lib/api';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';

export default function TrainingPage() {
  const [trainings, setTrainings] = useState([]);
  const { t } = useLanguage();
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [form, setForm] = useState({
    title: '', description: '', category: 'skill', trainer: '', start_date: '', end_date: '',
    duration_hours: '', location: '', max_participants: '', participants: [], cost: '',
    certification: false, certification_name: '', status: 'planned', notes: ''
  });

  const fetchData = async () => {
    try {
      const [trainRes, empRes] = await Promise.all([
        api.get('/training'),
        api.get('/employees'),
      ]);
      setTrainings(trainRes.data);
      setEmployees(empRes.data.filter(e => e.status === 'active' || e.active));
    } catch (err) {
      toast.error('Failed to load training data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!form.title) { toast.error('Title is required'); return; }
    try {
      const participantNames = form.participants.map(pid => employees.find(e => e.id === pid)?.name || '');
      await api.post('/training', {
        ...form,
        duration_hours: parseFloat(form.duration_hours) || 0,
        max_participants: parseInt(form.max_participants) || 0,
        cost: parseFloat(form.cost) || 0,
        participant_names: participantNames,
      });
      toast.success('Training program created');
      setShowDialog(false);
      setForm({ title: '', description: '', category: 'skill', trainer: '', start_date: '', end_date: '', duration_hours: '', location: '', max_participants: '', participants: [], cost: '', certification: false, certification_name: '', status: 'planned', notes: '' });
      fetchData();
    } catch (err) {
      toast.error('Failed to create training');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this training program?')) return;
    try {
      await api.delete(`/training/${id}`);
      toast.success('Training deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete');
    }
  };

  const handleStatusUpdate = async (id, status) => {
    try {
      await api.put(`/training/${id}`, { status });
      toast.success('Status updated');
      fetchData();
    } catch (err) {
      toast.error('Failed to update');
    }
  };

  const planned = trainings.filter(t => t.status === 'planned').length;
  const inProgress = trainings.filter(t => t.status === 'in_progress').length;
  const completed = trainings.filter(t => t.status === 'completed').length;
  const totalCost = trainings.reduce((s, t) => s + (t.cost || 0), 0);

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="training-page">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-4">

              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center shadow-lg shadow-blue-200/50">

                <BookOpen size={24} className="text-white" />

              </div>

              <div>

                <h1 className="text-2xl sm:text-3xl font-bold font-outfit tracking-tight text-foreground" data-testid="page-title">{t('training_title') || 'Training Management'}</h1>

                <p className="text-sm text-muted-foreground mt-0.5">Employee training management</p>

              </div>

            </div>
            <p className="text-muted-foreground text-sm mt-1">Manage training programs, certifications, and employee development.</p>
          </div>
          <Button onClick={() => setShowDialog(true)} data-testid="add-training-btn"><Plus size={14} className="mr-1.5" /> New Training</Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Card data-testid="stat-planned"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-info/10 rounded-lg"><Calendar size={18} className="text-info" /></div><div><div className="text-2xl font-bold">{planned}</div><div className="text-xs text-muted-foreground">Planned</div></div></div></CardContent></Card>
          <Card data-testid="stat-progress"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-warning/10 rounded-lg"><GraduationCap size={18} className="text-warning" /></div><div><div className="text-2xl font-bold">{inProgress}</div><div className="text-xs text-muted-foreground">In Progress</div></div></div></CardContent></Card>
          <Card data-testid="stat-completed"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-success/10 rounded-lg"><Award size={18} className="text-success" /></div><div><div className="text-2xl font-bold">{completed}</div><div className="text-xs text-muted-foreground">Completed</div></div></div></CardContent></Card>
          <Card data-testid="stat-cost"><CardContent className="pt-4 pb-3"><div className="flex items-center gap-3"><div className="p-2 bg-purple-50 rounded-lg"><DollarSign size={18} className="text-purple-600" /></div><div><div className="text-2xl font-bold">{totalCost.toLocaleString()}</div><div className="text-xs text-muted-foreground">Total Cost</div></div></div></CardContent></Card>
        </div>

        {/* Training List */}
        <Card>
          <CardHeader><CardTitle className="text-base">Training Programs</CardTitle></CardHeader>
          <CardContent>
            {trainings.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground text-sm">No training programs yet.</div>
            ) : (
              <div className="space-y-3">
                {trainings.map(t => (
                  <div key={t.id} className="border rounded-lg p-4 hover:bg-muted/50" data-testid={`training-${t.id}`}>
                    <div className="flex items-center justify-between mb-2">
                      <div>
                        <div className="font-medium text-sm flex items-center gap-2">
                          {t.title}
                          {t.certification && <Badge variant="outline" className="text-[10px]"><Award size={10} className="mr-0.5" /> Certified</Badge>}
                        </div>
                        <div className="text-xs text-muted-foreground mt-0.5">
                          {t.trainer && `Trainer: ${t.trainer} | `}{t.start_date} {t.end_date ? `- ${t.end_date}` : ''} | {t.duration_hours}h {t.location && `| ${t.location}`}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Select value={t.status} onValueChange={(v) => handleStatusUpdate(t.id, v)}>
                          <SelectTrigger className="w-32 h-8"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="planned">Planned</SelectItem>
                            <SelectItem value="in_progress">In Progress</SelectItem>
                            <SelectItem value="completed">Completed</SelectItem>
                            <SelectItem value="cancelled">Cancelled</SelectItem>
                          </SelectContent>
                        </Select>
                        <Button size="sm" variant="ghost" className="text-error" onClick={() => handleDelete(t.id)}><Trash2 size={14} /></Button>
                      </div>
                    </div>
                    {t.description && <div className="text-xs text-muted-foreground mb-2">{t.description}</div>}
                    <div className="flex items-center gap-4 text-xs text-muted-foreground">
                      <span className="flex items-center gap-1"><Users size={12} /> {(t.participants || []).length}{t.max_participants > 0 ? `/${t.max_participants}` : ''} participants</span>
                      {t.cost > 0 && <span className="flex items-center gap-1"><DollarSign size={12} /> {t.cost.toLocaleString()}</span>}
                      <span className="capitalize">{t.category}</span>
                    </div>
                    {(t.participant_names || []).length > 0 && (
                      <div className="flex flex-wrap gap-1 mt-2">
                        {t.participant_names.map((name, i) => <Badge key={i} variant="secondary" className="text-[10px]">{name}</Badge>)}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create Training Dialog */}
        <Dialog open={showDialog} onOpenChange={setShowDialog}>
          <DialogContent className="max-w-lg" data-testid="training-dialog">
            <DialogHeader><DialogTitle>New Training Program</DialogTitle></DialogHeader>
            <form onSubmit={handleCreate} className="space-y-3 max-h-[60dvh] overflow-y-auto pr-1">
              <div><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="training-title" required /></div>
              <div><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
              <div className="grid grid-cols-2 gap-3">
                <div><Label>Category</Label>
                  <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="skill">Skill Development</SelectItem><SelectItem value="compliance">Compliance</SelectItem><SelectItem value="safety">Safety</SelectItem><SelectItem value="leadership">Leadership</SelectItem><SelectItem value="technical">Technical</SelectItem><SelectItem value="onboarding">Onboarding</SelectItem></SelectContent>
                  </Select>
                </div>
                <div><Label>Trainer</Label><Input value={form.trainer} onChange={(e) => setForm({ ...form, trainer: e.target.value })} /></div>
                <div><Label>Start Date</Label><Input type="date" value={form.start_date} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></div>
                <div><Label>End Date</Label><Input type="date" value={form.end_date} onChange={(e) => setForm({ ...form, end_date: e.target.value })} /></div>
                <div><Label>Duration (hours)</Label><Input type="number" value={form.duration_hours} onChange={(e) => setForm({ ...form, duration_hours: e.target.value })} /></div>
                <div><Label>Location</Label><Input value={form.location} onChange={(e) => setForm({ ...form, location: e.target.value })} /></div>
                <div><Label>Max Participants</Label><Input type="number" value={form.max_participants} onChange={(e) => setForm({ ...form, max_participants: e.target.value })} /></div>
                <div><Label>Cost</Label><Input type="number" step="0.01" value={form.cost} onChange={(e) => setForm({ ...form, cost: e.target.value })} /></div>
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" checked={form.certification} onChange={(e) => setForm({ ...form, certification: e.target.checked })} id="cert-check" />
                <Label htmlFor="cert-check">Provides Certification</Label>
              </div>
              {form.certification && <div><Label>Certification Name</Label><Input value={form.certification_name} onChange={(e) => setForm({ ...form, certification_name: e.target.value })} /></div>}
              <div><Label>Notes</Label><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} rows={2} /></div>
              <Button type="submit" className="w-full" data-testid="training-submit">Create Training</Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
